import { useState, useEffect, useCallback } from 'react'
import { vouchersService } from '../services/vouchersService'
import type { BenefitVoucher, VoucherFilters, VoucherStatus } from '../types'

export function useVouchers(initialFilters?: Partial<VoucherFilters>) {
  const [data, setData] = useState<BenefitVoucher[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<VoucherFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  })

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await vouchersService.getAll(filters)
      setData(response.data)
      setTotal(response.total_items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar vales')
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const setPage = (page: number) => setFilters((prev) => ({ ...prev, page }))
  const setLimit = (limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))

  const setStatusFilter = (status: VoucherStatus | undefined) =>
    setFilters((prev) => ({ ...prev, status, page: 1 }))

  const setVoucherTypeFilter = (voucherType: string | undefined) =>
    setFilters((prev) => ({ ...prev, voucher_type: voucherType, page: 1 }))

  const setSearch = (search: string | undefined) =>
    setFilters((prev) => ({ ...prev, search: search || undefined, page: 1 }))

  const applyFilters = (partial: Partial<VoucherFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial, page: 1 }))

  const clearFilters = () =>
    setFilters({ page: 1, limit: filters.limit })

  return {
    data,
    total,
    isLoading,
    error,
    filters,
    setPage,
    setLimit,
    setStatusFilter,
    setVoucherTypeFilter,
    setSearch,
    applyFilters,
    clearFilters,
    refetch: fetchData,
  }
}
